import type { CompanyReviewsAISummary, Jobs, Reviews } from '@seek/libs-shared';
import type { CompanyDetails } from '../../../shared/services/companyDetailsService/types';
import type { CompaniesListModel } from '../../models/companiesListModel';
import { isTestAccount } from '../calculateShouldIndex';
import type { SalariesTabModel } from './salariesTabModel';

const mapToSalaryRanges = (
  companySalaryRange: CompanyDetails['companySalaryRange'],
) => {
  if (!companySalaryRange) {
    return undefined;
  }
  const { lastUpdatedAt, salaryRangeByRoles } = companySalaryRange;

  return {
    lastUpdated: lastUpdatedAt.shortAbsoluteLabel,
    salaryRangeByRoles: salaryRangeByRoles
      ?.filter((role) => !!role)
      .map(
        ({
          roleId,
          roleTitle,
          salaryRangeByCompany,
          salaryRangeByIndustry,
        }) => ({
          roleId,
          roleTitle,
          rangeByCompany: salaryRangeByCompany
            ? {
                ...salaryRangeByCompany,
              }
            : undefined,
          rangeByIndustry: salaryRangeByIndustry
            ? {
                ...salaryRangeByIndustry,
              }
            : undefined,
        }),
      ),
  };
};

export const mapSalariesModel = (
  companyId: string,
  companyDetails: CompanyDetails,
  jobsData: Jobs,
  randomCompaniesInThisIndustry: CompaniesListModel,
  companySalaryRangeResult: {
    data: CompanyDetails['companySalaryRange'];
    error: Error | null;
  },
  reviews: Reviews,
): SalariesTabModel => ({
  companyUrl: companyDetails.companyProfile?.data?.overview?.website?.url ?? '',
  companyName: companyDetails.companyProfile?.data?.name ?? '',
  // Map the salary satisfaction rating - same logic with review summary
  salarySatisfactionRating: (() => {
    const percentage1 =
      companyDetails.companyProfile?.companyReviewRating?.salaryFair
        ?.percentage ?? 0;
    const percentage2 =
      companyDetails.companyProfile?.companyReviewRating?.salaryGenerous
        ?.percentage ?? 0;
    const rating = percentage1 + percentage2;
    return rating > 0 ? rating : undefined;
  })(),
  jobsModel: {
    jobs: jobsData.jobs,
    totalJobs: jobsData.totalJobs,
    companyName: companyDetails.companyProfile?.data?.name ?? '',
    organisationId: companyDetails.companyProfile?.organisationId ?? undefined,
    companyId,
  },
  companyReviewsAISummary:
    (companyDetails.companyReviewsAISummary as CompanyReviewsAISummary) ??
    undefined,
  salaryRanges: mapToSalaryRanges(companySalaryRangeResult.data),
  randomCompaniesInThisIndustry,
  shouldIndex:
    Boolean(
      companySalaryRangeResult.data &&
        // TODO: Phase 1 only shows salary range by company, to be cleaned up at phase 2
        companySalaryRangeResult.data?.salaryRangeByRoles.filter((item) =>
          Boolean(item?.salaryRangeByCompany),
        ).length,
    ) && !isTestAccount(companyDetails),
  shouldShowErrorPage: Boolean(companySalaryRangeResult.error !== null),
  reviews: reviews ?? {
    companyId,
    name: companyDetails.companyProfile?.data?.name ?? '',
  },
});
